<script setup>
import { useCalendar, useDayjs } from '@daysmart/daysmart-vue-components/composables'
import { useCalendarViewState } from '../composables/useCalendarViewState'
import { useCalendarSettings } from '../composables/useCalendarSettings'

const props = defineProps({
  date: {
    type: String,
    required: true,
  },
})
const emit = defineEmits(['update:date'])
const dayjs = useDayjs()
const view = useCalendarViewState()
const { customFirstDayOfWeek } = useCalendarSettings()
const { getWeekView } = useCalendar({ customFirstDayOfWeek })
const { t } = useI18n()

const value = computed({
  get() {
    return props.date
  },
  set(value) {
    emit('update:date', value)
  },
})
const currentDate = computed(() => dayjs.value(props.date))
const currentMonth = computed(() => currentDate.value.month())
const currentYear = computed(() => currentDate.value.year())
const currentMonthName = computed(() => currentDate.value.format('MMMM'))
const currentDay = computed(() => currentDate.value.date())
const currentWeek = computed(() => {
  const currentWeek = getWeekView.value(currentDate.value.format('YYYY-MM-DD'))
  return {
    start: currentWeek[0],
    end: currentWeek[currentWeek.length - 1],
  }
})

const prevInfo = computed(() => {
  switch (view.value) {
    case 'month':
      return currentDate.value.subtract(1, 'month').format('MMMM')
    case 'week':
    case 'day':
      if (currentWeek.value?.start && currentWeek.value?.end)
        return `${currentWeek.value.start.subtract(1, 'week').format('ddd D')} - ${currentWeek.value.end.subtract(1, 'week').format('ddd D')}`
  }
  return ''
})

const nextInfo = computed(() => {
  switch (view.value) {
    case 'month':
      return currentDate.value.add(1, 'month').format('MMMM')
    case 'week':
    case 'day':
      if (currentWeek.value?.start && currentWeek.value?.end)
        return `${currentWeek.value.start.add(1, 'week').format('ddd D')} - ${currentWeek.value.end.add(1, 'week').format('ddd D')}`
  }
  return ''
})

function change(mode) {
  let unit = view.value
  if (view.value === 'day')
    unit = 'week'

  const newDate = mode === 'prev' ? currentDate.value.subtract(1, unit) : currentDate.value.add(1, unit)
  value.value = newDate.format('YYYY-MM-DD')
}
</script>

<template>
  <div class="calendar-date-control mb-2 min-w-min flex items-center justify-between flex-gap-x-4 sm:(rounded-2 bg-surface-400 p-2 ring-1 ring-surface-600 ring-inset)">
    <div class="prev-month flex items-center">
      <button
        class="btn-md h-32px flex items-center rounded-2 px-2 py-1 ring-1 ring-surface-600 sm:(h-40px px-3) btn-alt-outline-secondary"
        :aria-label="t('Previous')"
        @click="change('prev')"
      >
        <DSIcon
          icon="i-far-chevron-left"
          size="24"
        />
      </button>
      <p class="ml-4 whitespace-nowrap text-lg text-text-400 hidden sm:block">
        {{ prevInfo }}
      </p>
    </div>
    <div class="current-month flex items-center">
      <p
        v-if="view === 'week' || view === 'day'"
        class="whitespace-nowrap text-secondary-400 lt-sm:text-lg sm:text-2xl"
      >
        <i18n-d
          tag="span"
          :value="currentWeek.start.toDate()"
          format="medium_without_year"
        >
          <template #month="{ month }">
            <span class="fw-600">{{ month }}</span>
          </template>
          <template #year="{ year }">
            <span class="sr-only">{{ year }}</span>
          </template>
        </i18n-d>
        <span> - </span>
        <i18n-d
          tag="span"
          :value="currentWeek.end.toDate()"
          format="medium_without_year"
        >
          <template #month="{ month }">
            <span class="fw-600">{{ month }}</span>
          </template>
          <template #year="{ year }">
            <span class="sr-only">{{ year }}</span>
          </template>
        </i18n-d>
      </p>
      <p
        v-else-if="view === 'month'"
        class="whitespace-nowrap text-secondary-400 lt-sm:text-lg sm:text-2xl"
      >
        <span class="font-bold">{{ currentMonthName }}</span> {{ currentYear }}
      </p>
      <DSDateCalendarInput
        v-model="value"
        :deselect="false"
      >
        <button
          class="ml-2 btn-sm ring-secondary-400 sm:flex !px-1 !py-.5 lt-sm:hidden btn-alt-outline-secondary"
          :aria-label="t('Select a Date')"
        >
          <DSIcon
            icon="i-fas-calendar-week"
            size="24"
          />
        </button>
      </DSDateCalendarInput>
    </div>
    <div class="next-month flex items-center">
      <p class="mr-4 whitespace-nowrap text-lg text-text-400 hidden sm:block">
        {{ nextInfo }}
      </p>
      <button
        class="btn-md h-32px flex items-center rounded-2 px-2 py-1 ring-1 ring-surface-600 sm:(h-40px px-3) btn-alt-outline-secondary"
        :aria-label="t('Next')"
        @click="change('next')"
      >
        <DSIcon
          icon="i-far-chevron-right"
          size="24"
        />
      </button>
    </div>
  </div>
</template>

<i18n>
{
  "en-us": {
    "Previous": "Previous",
    "Next": "Next",
    "Select a Date": "Select a Date"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Previous": "Zurück",
  "Next": "Weiter",
  "Select a Date": "Datum auswählen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Previous": "Précédent",
  "Next": "Suivant",
  "Select a Date": "Sélectionner une date"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Previous": "Atrás",
  "Next": "Siguiente",
  "Select a Date": "Seleccionar una fecha"
}
</i18n>
